//  External libs
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from 'react-router-dom';  // To navigate to the main app after login
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faLightbulb, faCode, faPowerOff } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import { useDetectClickOutside } from "react-detect-click-outside";

// Assets
import "./DialogComponent.css";

/**
 * A dialog component that displays a set of buttons and handles user interactions.
 *
 * @param {Function} onButtonClick - A callback function to handle button clicks.
 * @param {Array} devices - A list of devices to be displayed or interacted with within the dialog.
 * @param {boolean} disableEdit - A flag that controls whether the "Edit" button should be disabled.
 * @param {boolean} disableIdentify - A flag that controls whether the "Identify" button should be disabled.
 * @param {boolean} disableFirmware - A flag that controls whether the "Firmware" button should be disabled.
 * @param {Function} toggleShow - A callback function to toggle the visibility of the dialog.
 * @param {string} id - The unique identifier for the dialog component, normally the device id but can be anything, e.g. for the "all" checkbox rows
 *
 * @returns {JSX.Element} The rendered dialog component.
 *
 * @example
 * <DialogComponent
 *   onButtonClick={handleClick}
 *   devices={deviceList}
 *   disableEdit={false}
 *   disableIdentify={true}
 *   disableFirmware={false}
 *   toggleShow={handleToggleShow}
 *   id="device-dialog-1"
 * />
 */
const DialogComponent = ({
  onButtonClick,
  devices,
  disableEdit,
  disableIdentify,
  disableFirmware,
  toggleShow,
  id,
  translateY,
}) => {
  const navigate = useNavigate();
  const ref = useDetectClickOutside({ onTriggered: onButtonClick });

  const [transformStyle, setTransformStyle] = useState({
    transform: `translate(-110px, 0px)`,
  });

  /* use effect functions */
  useEffect(() => {
    const newTransformStyle = {
      transform: `translate(-85px, ${-translateY}px)`,
    };
    setTransformStyle(newTransformStyle);
  }, [translateY]);

  /**
   * Returns an object with a `disabled` property to control the disabled state of a button.
   *
   * @param {boolean} flag - A boolean value that determines whether the button should be disabled.
   * @returns {Object} An object with the `disabled` property set to `true` or `false` based on the `flag`.
   */
  const disableButton = (flag) => {
    if (flag) {
      return { disabled: "disabled" };
    } else {
      return {};
    }
  };

  if (toggleShow) {
    return (
      <div className="dialog" ref={ref} style={transformStyle}>
        {/* edit button */}
        <button
          className="dialog-btn top-btn"
          id={`edit_btn_${id}`}
          {...disableButton(disableEdit)}
          onClick={() => onButtonClick("edit", devices)}
        >
          <span className="dialog-icon">
            <FontAwesomeIcon icon={faPen} size="xs" />
          </span>{" "}
          Edit
        </button>
        <div className="dialog_tooltip">
          <Tooltip anchorSelect={`#edit_btn_${id}`} place="left">
            Edit device configuration
          </Tooltip>
        </div>

        {/* identify button */}
        <button
          className="dialog-btn"
          id={`identify_btn_${id}`}
          {...disableButton(disableIdentify)}
          onClick={() => onButtonClick("identify", devices)}
        >
          <span className="dialog-icon">
            <FontAwesomeIcon icon={faLightbulb} size="xs" />
          </span>{" "}
          Identify
        </button>
        <div className="dialog_tooltip">
          <Tooltip anchorSelect={`#identify_btn_${id}`} place="left">
            Device LED will flash for 15 seconds
          </Tooltip>
        </div>

        {/* firmware button */}
        <button
          className="dialog-btn"
          id={`fw_btn_${id}`}
          {...disableButton(disableFirmware)}
          onClick={() => onButtonClick("firmware", devices)}
        >
          <span className="dialog-icon">
            <FontAwesomeIcon icon={faCode} size="xs" />
          </span>{" "}
          Firmware
        </button>
        <div className="dialog_tooltip">
          <Tooltip anchorSelect={`#fw_btn_${id}`} place="left">
            Update device firmware
          </Tooltip>
        </div>

        {/* reboot button */}
        <button
          className="dialog-btn"
          id={`reboot_btn_${id}`}
          {...disableButton(disableFirmware)}
          onClick={() => onButtonClick("reboot", devices)}
        >
          <span className="dialog-icon">
            <FontAwesomeIcon icon={faPowerOff} size="xs" />
          </span>{" "}
          Reboot
        </button>
        <div className="dialog_tooltip">
          <Tooltip anchorSelect={`#reboot_btn_${id}`} place="left">
            Reboot the device
          </Tooltip>
        </div>

        {/* device details page  button */}
        <button
          className="dialog-btn"
          id={`deviceDetail_btn_${id}`}
          onClick={() => navigate(`/devices/${id}`)}
        >
          <span className="dialog-icon">
            <FontAwesomeIcon icon={faPowerOff} size="xs" />
          </span>{" "}
          Details
        </button>
        <div className="dialog_tooltip">
          <Tooltip anchorSelect={`#deviceDetail_btn_${id}`} place="left">
            Details of the device
          </Tooltip>
        </div>

      </div>
    );
  } else {
    return null;
  }
};

DialogComponent.propTypes = {
  onButtonClick: PropTypes.func.isRequired,
  devices: PropTypes.arrayOf(PropTypes.object).isRequired,
  disableEdit: PropTypes.bool,
  disableIdentify: PropTypes.bool,
  disableFirmware: PropTypes.bool,
  toggleShow: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
};

export default DialogComponent;
